import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatApiDetail, getAdminLegalIdentity, updateAdminLegalIdentity } from '../../lib/api';
import { LEGAL_IDENTITY_FIELDS, invalidateLegalIdentity } from '../../lib/legalIdentity';

const LABELS = Object.fromEntries(LEGAL_IDENTITY_FIELDS.map((f) => [f.key, f.label]));

/**
 * Admin → Sitio público → «Identidad y contacto» (paquete legal v2026-10-09, doc. 06 §1).
 * Única fuente del responsable y del contacto del sitio (cabecera, pie, /contacto, textos legales, WhatsApp).
 * Solo super_admin puede guardar (el backend lo exige). Mientras falten obligatorios: «no publicable».
 */
export default function LegalIdentityCard({ onSaved }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const d = await getAdminLegalIdentity();
    setData(d);
    const next = { responsible_mode: d.responsible_mode || 'provider', demo_site: d.demo_site !== false };
    LEGAL_IDENTITY_FIELDS.forEach((f) => {
      next[f.key] = d[f.key] ?? '';
    });
    setDraft(next);
  };

  useEffect(() => {
    load().catch(() => toast.error('No se pudo cargar «Identidad y contacto».'));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const d = await updateAdminLegalIdentity(draft);
      setData(d);
      invalidateLegalIdentity();
      toast.success(d.publishable ? 'Identidad guardada. Textos publicables.' : 'Identidad guardada (faltan datos obligatorios).');
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo guardar.'));
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return <p className="text-sm text-muted-foreground animate-pulse">Cargando «Identidad y contacto»…</p>;
  }

  const missing = data?.missing_required || [];

  return (
    <Card className="scroll-mt-24" id="identidad-y-contacto" data-testid="legal-identity-card">
      <CardHeader>
        <CardTitle>Identidad y contacto</CardTitle>
        <p className="text-sm text-muted-foreground">
          Estos datos alimentan el aviso de privacidad, los términos, la política de cookies, el pie de página,
          /contacto y los mensajes de WhatsApp. No hay datos fijos en el código: un campo vacío se muestra como
          «dato pendiente».
        </p>
        <p
          className={`mt-2 text-sm font-semibold rounded-md px-3 py-2 ${
            missing.length ? 'bg-amber-50 text-amber-900 border border-amber-300' : 'bg-emerald-50 text-emerald-900 border border-emerald-300'
          }`}
          data-testid="legal-identity-status"
        >
          {missing.length
            ? `No publicable: faltan ${missing.map((k) => LABELS[k] || k).join('; ')}.`
            : 'Publicable: todos los datos obligatorios están completos.'}
        </p>
        {data?.wa_ai_enabled ? (
          <p className="text-xs text-muted-foreground mt-1">
            El asistente con IA de WhatsApp está activo: el aviso muestra sus párrafos y el proveedor de IA es obligatorio.
          </p>
        ) : null}
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="li-mode">Modo</Label>
              <select
                id="li-mode"
                className="w-full border rounded-md px-3 py-2 bg-background"
                value={draft.responsible_mode}
                onChange={(e) => setDraft({ ...draft, responsible_mode: e.target.value })}
              >
                <option value="provider">DanySolutions es el responsable (sitio de demostración)</option>
                <option value="clinic">El negocio es el responsable; DanySolutions es encargado</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm pt-7" htmlFor="li-demo">
              <input
                id="li-demo"
                type="checkbox"
                checked={draft.demo_site}
                onChange={(e) => setDraft({ ...draft, demo_site: e.target.checked })}
              />
              Sitio de demostración (muestra el banner superior)
            </label>
            {LEGAL_IDENTITY_FIELDS.map((f) => (
              <div
                key={f.key}
                className={`space-y-2 ${['address', 'pickup_address', 'judicial_district'].includes(f.key) ? 'sm:col-span-2' : ''}`}
              >
                <Label htmlFor={`li-${f.key}`}>
                  {f.label}
                  {f.required ? ' *' : ''}
                </Label>
                <Input
                  id={`li-${f.key}`}
                  value={draft[f.key] ?? ''}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  placeholder={f.placeholder || undefined}
                  aria-invalid={missing.includes(f.key) ? 'true' : undefined}
                  aria-describedby={f.hint ? `li-${f.key}-hint` : undefined}
                  className={missing.includes(f.key) ? 'border-amber-500' : ''}
                />
                {f.hint ? (
                  <p id={`li-${f.key}-hint`} className="text-xs text-muted-foreground">
                    {f.hint}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="rounded-full">
              {saving ? 'Guardando…' : 'Guardar identidad y contacto'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
